import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { ArrowLeft, Loader2, TrendingUp, AlertTriangle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const pctColor = (p) => p >= 70 ? 'text-emerald-600 bg-emerald-50' : p >= 40 ? 'text-amber-600 bg-amber-50' : 'text-rose-600 bg-rose-50';

export default function TeacherStudentProfilePage() {
    const { classroomId, studentId } = useParams();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get(`/api/teacher/classrooms/${classroomId}/students/${studentId}`).then((r) => setData(r.data)).catch(() => setData(false));
    }, [classroomId, studentId]);

    if (data === null) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#7C3AED] animate-spin" /></div>;
    if (data === false) return <div className="text-sm text-zinc-500">Student not found.</div>;

    const chartData = data.chart.map((c, i) => ({
        ...c,
        label: c.submitted_at ? new Date(c.submitted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : `#${i + 1}`,
    }));

    return (
        <div className="space-y-6" data-testid="student-profile-page">
            <Link to={`/teacher/classes/${classroomId}`} className="inline-flex items-center gap-2 text-sm font-bold text-zinc-500 hover:text-[#7C3AED]" data-testid="back-classroom">
                <ArrowLeft className="w-4 h-4" /> Back to class
            </Link>

            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{data.name}</h1>
                    <p className="text-sm text-zinc-500 mt-1">{data.email}</p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-zinc-500">{data.submission_count} submissions</span>
                    <span className={`text-xs font-black px-3 py-1.5 rounded-md ${pctColor(data.avg_pct || 0)}`}>Avg {Math.round(data.avg_pct || 0)}%</span>
                </div>
            </div>

            {/* Progress chart */}
            <div className="bg-white rounded-3xl border border-zinc-200 p-6">
                <h2 className="font-black text-base mb-4 inline-flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#7C3AED]" /> Progress over time</h2>
                {chartData.length === 0 ? (
                    <p className="text-sm text-zinc-500 py-8 text-center">No submissions yet from this student.</p>
                ) : (
                    <div style={{ width: '100%', minWidth: 0, height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#EDE4FF" />
                                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                                <Tooltip contentStyle={{ borderRadius: 8, borderColor: '#7C3AED' }} />
                                <Line type="monotone" dataKey="pct" stroke="#7C3AED" strokeWidth={3} dot={{ fill: '#7C3AED', r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* Weaknesses */}
            {data.weaknesses.length > 0 && (
                <div className="bg-white rounded-3xl border border-zinc-200 p-6">
                    <h2 className="font-black text-base mb-4 inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Recurring weaknesses</h2>
                    <ul className="space-y-2">
                        {data.weaknesses.map((w, i) => (
                            <li key={i} className="flex items-center justify-between text-sm">
                                <span className="flex-1 text-zinc-700">{w.text}</span>
                                <span className="text-xs font-bold text-amber-600">×{w.count}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Submissions */}
            <div className="bg-white rounded-3xl border border-zinc-200 p-6">
                <h2 className="font-black text-base mb-4">All submissions</h2>
                {data.submissions.length === 0 ? (
                    <p className="text-sm text-zinc-500 py-8 text-center">No submissions yet.</p>
                ) : (
                    <ul className="divide-y divide-zinc-100">
                        {data.submissions.slice().reverse().map((s) => (
                            <li key={s.id}>
                                <Link to={`/teacher/submissions/${s.id}`} className="flex items-center justify-between py-3 hover:bg-[#F8F7FC] rounded-lg px-2 -mx-2 transition-colors" data-testid={`student-sub-${s.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-zinc-400 mb-0.5">
                                            {s.subject}
                                            {s.paper_year ? ` · Paper ${s.paper_year}` : ''}
                                            {s.series ? ` ${s.series}` : ''}
                                            {s.question_number ? ` · Q${s.question_number}` : ''}
                                            {' · '}{s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : ''}
                                        </div>
                                        <div className="text-sm font-bold text-zinc-700 truncate">{s.question}</div>
                                    </div>
                                    <span className={`text-xs font-black px-2 py-1 rounded-md flex-shrink-0 ${pctColor(s.pct)}`}>{Math.round(s.pct)}%</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
